/* Copyright (c) 2023 MOCA Systems, Inc. All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Functions } from '@angular/fire/functions';

import { FirebaseCloudFunctionsService } from './cloud-functions.service';
import {
	ICreateOrganizationResult,
	ICreateOrganizationParams,
	IChangeOrganizationParams,
	IDeleteOrganizationParams,
	IAddProjectToOrganizationParams,
} from '@touchplan/common';

@Injectable({
	providedIn: 'root',
})
export class OrganizationFirebaseCloudFunctionsService {
	private functionsInstance: Functions = inject(Functions);

	/**
	 * Creates an organization application
	 * @param optionalNewId New ID to use for the created organization application, or null if a new ID should be generated
	 * @param name Name of the new organization
	 * @param description Description of the organization
	 * @param url Url of the organization
	 * @param parentOrganizationId parent organization id (top level organization)
	 * @param projectIds list of project ids to
	 * @returns organization id and it's api secret
	 */
	public createOrganization(
		optionalNewId: string,
		name: string,
		description: string,
		url: string,
		parentOrganizationId: string,
		projectIds: string[]
	): Observable<ICreateOrganizationResult> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<
			ICreateOrganizationParams,
			ICreateOrganizationResult
		>(this.functionsInstance, 'mb4-createOrganization', {
			optionalNewId,
			name,
			description,
			url,
			parentOrganizationId,
			projectIds,
		});
	}

	/**
	 * Creates a new API secret for the given organization's user, change the user's password to that
	 * and return the new secret
	 * @param organizationId organization id (child organization)
	 * @returns new organization API secret
	 */
	public changeOrganizationSecret(organizationId: string): Observable<string> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<string, string>(
			this.functionsInstance,
			'mb4-changeOrganizationSecret',
			organizationId
		);
	}

	/**
	 * Changes the details of an organization.
	 *
	 * @param organizationId - The ID of the organization to be changed.
	 * @param name - The new name for the organization.
	 * @param description - The new description for the organization.
	 * @param url - The new URL for the organization.
	 * @param projectIds - An array of project IDs associated with the organization.
	 * TODO: Verify and handle the response appropriately.
	 */
	public changeOrganization(
		organizationId: string,
		name: string,
		description: string,
		url: string,
		projectIds: string[]
	): Observable<void> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<IChangeOrganizationParams, void>(
			this.functionsInstance,
			'mb4-changeOrganization',
			{
				organizationId,
				name,
				description,
				url,
				projectIds,
			}
		);
	}

	/**
	 * Adds a project to an organization.
	 *
	 * @param organizationId - The ID of the organization to which the project will be added.
	 * @param projectId - The ID of the project to be added to the organization.
	 * TODO: Verify and handle the response appropriately.
	 */
	public addProjectToOrganization(organizationId: string, projectId: string): Observable<void> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<IAddProjectToOrganizationParams, void>(
			this.functionsInstance,
			'mb4-addProjectToOrganization',
			{
				organizationId,
				projectId,
			}
		);
	}

	/**
	 * Deletes an organization with the specified ID.
	 *
	 * @param organizationId - The ID of the organization to be deleted.
	 * TODO: Verify and handle the response appropriately.
	 */
	public deleteOrganization(organizationId: string): Observable<void> {
		return FirebaseCloudFunctionsService.callFunctionAsObservable$<IDeleteOrganizationParams, void>(
			this.functionsInstance,
			'mb4-deleteOrganization',
			{
				organizationId,
			}
		);
	}
}
